import React from 'react'
import styled from 'styled-components';

const Document = styled.img`
  display: none;
  height: 70px;
  width: fit-content;
  background-color: ${({ theme }) => theme.card};
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

const Card = styled.div`
  width: 650px;
  border-radius: 16px;
  padding: 16px 20px;
  justify-content: center;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: all 0.4s ease-in-out;
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.glass_border};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${({ theme }) => theme.gradient_primary};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    box-shadow: 0 20px 40px ${({ theme }) => theme.shadow_primary};
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.primary + '50'};
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  &:hover ${Document} {
    display: flex;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 14px 16px;
    gap: 12px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 14px;
    gap: 10px;
  }
`;

const Top = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const Logo = styled.img`
  height: 50px;
  width: 50px;
  object-fit: contain;
  background-color: ${({ theme }) => theme.bg};
  border-radius: 12px;
  margin-top: 4px;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.glass_border};

  @media (max-width: 768px) {
    height: 40px;
    width: 40px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
`;

const Role = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  transition: color 0.3s ease;
  
  ${Card}:hover & {
    color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Company = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Duration = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
  font-family: 'JetBrains Mono', monospace;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const Description = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Skills = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 8px;
  }
`;

const SkillsLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Skill = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '15'};
  padding: 4px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primary + '30'};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 3px 8px;
  }
`;

const ExperienceCard = ({ experience }) => {
  return (
    <Card>
      <Top>
        <Body>
          <Role>{experience.role}</Role>
          <Company>{experience.company}</Company>
          <Duration>{experience.date}</Duration>
        </Body>
      </Top>
      <Description>
        {experience.desc}
        {experience?.skills && (
          <>
            <br />
            <Skills>
              <SkillsLabel>Skills:</SkillsLabel>
              <ItemWrapper>
                {experience.skills.map((skill, index) => (
                  <Skill key={index}>{skill}</Skill>
                ))}
              </ItemWrapper>
            </Skills>
          </>
        )}
      </Description>
      {experience.doc && (
        <a href={experience.doc} target='_blank' rel="noopener noreferrer">
          <Document src={experience.doc} alt="Document" />
        </a>
      )}
    </Card>
  )
}

export default ExperienceCard;